import { normalize, denormalize, schema } from 'normalizr';
import { format, compareDesc } from 'date-fns';
import _groupBy from 'lodash/groupBy';
import _get from 'lodash/get';
import {
  getActivities,
  getNewActivities,
  markActivityAsRead,
  markActivitiesAsSeen,
} from '@homeservices/api-kit/notifications/activities/v1';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';
import { MATCH_STATUS_ALIASES } from '@/constants/match-statuses';

const notificationSchema = new schema.Entity('notification');
const DEFAULT_PAGE_SIZE = 10;
const NOTIFICATION_ROUTE_MAPPING = new Map();
NOTIFICATION_ROUTE_MAPPING.set('potential-jobs', { name: 'dashboard::pro::jobs', params: { jobsStatusAlias: MATCH_STATUS_ALIASES.potential.key } });
NOTIFICATION_ROUTE_MAPPING.set('hired-jobs', { name: 'dashboard::pro::jobs', params: { jobsStatusAlias: MATCH_STATUS_ALIASES.hired.key } });
NOTIFICATION_ROUTE_MAPPING.set('compliance-review-warning', { name: 'auth-redirect', query: { source: 'application_status' } });

export default {
  namespaced: true,
  state () {
    return {
      meta: {
        hasUnseen: false,
        hasMore: true,
        loading: false,
      },
      notifications: {},
    };
  },
  getters: {
    notificationsMetadata (state) {
      return state.meta;
    },
    getNotificationById: state => (activityId) => {
      let notification = denormalize(state.notifications[activityId], notificationSchema, state);
      // Transformation
      notification = {
        ...notification,
        created_dtm: addUTCTimeZoneDesignator(notification.created_dtm),
        modified_dtm: addUTCTimeZoneDesignator(notification.modified_dtm),
      };
      return notification;
    },
    notifications (state, getters) {
      return Object.keys(state.notifications)
        .map(getters.getNotificationById)
        .sort((a, b) => compareDesc(new Date(a.created_dtm), new Date(b.created_dtm)));
    },
    getNotificationRouteById: (state, getters) => (activityId) => {
      const notification = getters.getNotificationById(activityId);
      const type = _get(notification, 'data.type');
      return NOTIFICATION_ROUTE_MAPPING.get(type);
    },
    dateGroupedNotifications (state, getters) {
      const { notifications } = getters;
      const groupedNotifications = _groupBy(
        notifications,
        notification => format(new Date(notification.created_dtm), 'MM/yyyy')
      );

      return Object.keys(groupedNotifications)
        .sort((a, b) => compareDesc(new Date(a.created_dtm), new Date(b.created_dtm)))
        .map((date) => {
          const datesInADay = groupedNotifications[date];
          return datesInADay.sort((a, b) =>
            compareDesc(new Date(a.created_dtm), new Date(b.created_dtm)));
        });
    },
  },
  actions: {
    async fetchNotifications ({ commit }, { size = DEFAULT_PAGE_SIZE, ...params } = {}) {
      commit('updateMeta', { loading: true });
      const { data: { results: notifications } } = await getActivities('my-notifications', {
        size,
        ...params,
      });

      const { entities } = normalize(notifications, [notificationSchema]);

      commit('updateMeta', {
        hasMore: notifications.length === size,
        loading: false,
      });
      commit('updateNotificationState', { notifications: entities.notification });
    },
    async fetchMoreNotifications ({ dispatch, state }, params = {}) {
      const currentNumberOfNotifications = Object.keys(state.notifications).length;

      dispatch('fetchNotifications', {
        ...params,
        from: currentNumberOfNotifications,
      });
    },
    async markNotificationAsRead ({ commit, state }, activityId) {
      const notification = state.notifications[activityId];
      if (notification.read) { return; }

      commit('setReadFlag', { activityId, value: true });
      try {
        await markActivityAsRead(activityId);
      } catch (err) {
        commit('setReadFlag', { activityId, value: false });
      }
    },
    async fetchUnseenNotifications ({ commit }, params) {
      const { data: { results: notifications } } = await getNewActivities('my-notifications', {
        size: 1,
        ...params,
      });

      commit('updateMeta', { hasUnseen: Boolean(notifications.length) });
    },
    async markAllNotificationsAsSeen ({ commit, dispatch }) {
      commit('updateMeta', { hasUnseen: false });

      try {
        await markActivitiesAsSeen('my-notifications');
      } catch (err) {
        dispatch('fetchUnseenNotifications');
        // TODO: fire general "an error occured" message
      }
    },
  },
  mutations: {
    updateNotificationState (state, { notifications }) {
      state.notifications = {
        ...state.notifications,
        ...notifications,
      };
    },
    updateMeta (state, payload) {
      state.meta = {
        ...state.meta,
        ...payload,
      };
    },
    setReadFlag (state, { activityId, value }) {
      state.notifications = {
        ...state.notifications,
        [activityId]: {
          ...state.notifications[activityId],
          read: value,
        },
      };
    },
  },
};
